import { ProjectSettingsModeGroupEnum } from '@/app/api';
import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import { CoinsIcon } from '@/app/components/CoinsIcon/CoinsIcon';
import useAnalytics from '@/app/hooks/useAnalytics';
import { useDonateLink } from '@/app/hooks/useDonateLink';
import { useIsProjectActive, useProjectData } from '@/app/providers/ProjectProvider';
import { formatPriceInCents } from '@/app/utils/formatting';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

type Props = {
  activeGoodHabitAmount?: number;
  className?: string;
  onCommercialDonateClick?: () => void;
};

export const SupportStoryWidget = ({ activeGoodHabitAmount, className, onCommercialDonateClick }: Props) => {
  const { project } = useProjectData();
  const { t } = useTranslation();
  const {
    projectDetail: { supportStoryWidgetClicked },
  } = useAnalytics();
  const thankYouText = project.content.texts.thankYouText;
  const image = project.content.mainPhoto?.sizes.fullCropped.path;
  const isProjectActive = useIsProjectActive();

  const donateLink = useDonateLink(activeGoodHabitAmount);
  const isProjectPlus = project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus;

  return (
    <div
      className={classNames(
        className,
        'justify-center h-max  lg:w-[720px] max-w-full lg:max-w-screen-xxl flex gap-[32px] my-[40px]',
      )}
    >
      <div className={classNames('flex h-full  lg:min-h-[240px] flex-col lg:h-full lg:justify-between w-full')}>
        <div className="mb-[24px] h-full lg:mb-0 lg:w-full">
          <h2 className="text-[32px] leading-[120%] mb-[16px]">
            {t('components.SupportStoryWidget.titleTemplate', { text: thankYouText })}
          </h2>
          <img
            src={image}
            alt="main photo image"
            className="lg:hidden mb-[24px] h-auto w-full object-cover rounded-[4px]"
          />

          <div className="flex items-center gap-[8px] mb-[16px]">
            <img src="/public/images/heart-on-palm.gif" alt="donation badge" className="mt-[8px] h-[25px] w-[25px]" />
            <p>{t('components.SupportStoryWidget.weWillHandOverFullAmount')}</p>
          </div>
          {project.settings.modeGroup !== ProjectSettingsModeGroupEnum.Plus && (
            <div className="flex items-center gap-[8px] mb-[16px]">
              <CoinsIcon className="text-success-mid h-[24px] w-[24px]" />
              <p>{t('components.SupportStoryWidget.evenTheSmallestDonationCanBringHopes')}</p>
            </div>
          )}
        </div>
        <div>
          {isProjectPlus ? (
            <Button
              testId="support-story-donate-button"
              style={ButtonStyle.PRIMARY}
              className={classNames('text-center px-[24px] py-[20px] tracking-[4px] w-full block')}
              tagSpecificProps={{
                htmlTag: ButtonHTMLTag.BUTTON,
                disabled: !isProjectActive,
                onClick: () => {
                  supportStoryWidgetClicked();
                  onCommercialDonateClick?.();
                },
              }}
            >
              {t('components.DonateBox.donateCommercial')}
            </Button>
          ) : (
            <Button
              testId="support-story-donate-button"
              style={ButtonStyle.PRIMARY}
              className={classNames('text-center px-[24px] py-[20px] tracking-[4px] w-full block')}
              tagSpecificProps={{
                htmlTag: ButtonHTMLTag.LINK,
                href: donateLink,
                disabled: !isProjectActive,
                onClick: supportStoryWidgetClicked,
              }}
            >
              {t(activeGoodHabitAmount ? 'components.DonateBox.donateAmount' : 'components.DonateBox.donate', {
                amount: formatPriceInCents(activeGoodHabitAmount ?? 0),
              })}
            </Button>
          )}
        </div>
      </div>
      <img
        src={image}
        alt="main photo image"
        className="hidden lg:block h-full lg:h-[240px] w-[382px] object-cover rounded-[4px]"
      />
    </div>
  );
};
